import { Scale } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";

import { StatusLine } from "./StatusLine";
import { SWAP } from "./swap";
import { BigNumber, Tile, TileAction, type TileEdit } from "./Tile";
import { useTilePulse } from "./useTilePulse";
import { useShortTokenFormat, useTokenFormat } from "./useTokenFormat";
import { getCaptainBalanceState } from "../../balance-state";
import { CaptainTokensIcon } from "../../constants";
import { useTopUpSpent } from "../../hooks/useTopUpSpent";
import type { CaptainBillingAccount, CaptainBillingHistorySource } from "../../types";
import { balanceTracks, capResetDate } from "../../utils";
import { CAP_FILL } from "../balance-tab-variants";
import { BalanceSwatch } from "../BalanceSwatch";
import { BalanceTracks } from "../BalanceTracks";
import { CorrectionEditor } from "../editors/CorrectionEditor";

import { formatDayMonth } from "@/utils/dateUtils";

export function AvailableTile({ account, edit }: { account: CaptainBillingAccount; edit?: TileEdit }) {
  const { t, i18n } = useTranslation();
  const state = getCaptainBalanceState(account);
  const { spent: spentFromTopUp } = useTopUpSpent(account);
  const tracks = balanceTracks(account, spentFromTopUp);
  const pulse = useTilePulse(account.availableTokens);
  const usedPercent = Math.round(tracks.usedPercent);
  const leftPercent = 100 - usedPercent;
  const pausedKey = `${edit ? "admin" : "manager"}${account.monthlyCapTokens > 0 ? "" : "NoCap"}`;
  const statusText =
    state === "low"
      ? t("captainBilling.tiles.status.low", { percent: leftPercent })
      : t(`captainBilling.tiles.status.paused.${pausedKey}`, {
        date: formatDayMonth(capResetDate(account.month), i18n.language, "short"),
      });

  return (
    <Tile
      icon={CaptainTokensIcon}
      label={t("captainBilling.tiles.available")}
      hint={t("captainBilling.tiles.hint.available")}
      pulse={pulse}
      corner={
        edit &&
        !edit.isEditing && <TileAction label={t("captainBilling.actions.correct")} icon={Scale} onClick={edit.onEdit} />
      }
    >
      <AnimatePresence mode="wait" initial={false}>
        {edit?.isEditing ? (
          <motion.div key="edit" {...SWAP}>
            <CorrectionEditor account={account} onDone={edit.onDone} />
          </motion.div>
        ) : (
          <motion.div key="view" {...SWAP} className="space-y-2.5">
            <BigNumber value={account.availableTokens} qualifier={t("captainBilling.tiles.tokens")} />
            <BalanceTracks tracks={tracks} capClass={CAP_FILL[state]} />
            {state === "healthy" ? (
              <p className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground tabular-nums">
                <LegendItem source="cap" text={(format) => t("captainBilling.tiles.fromCap", {
                  tokens: format(Math.max(0, account.capLeftTokens)),
                  total: format(account.monthlyCapTokens),
                })} />
                <LegendItem source="topUp" text={(format) => spentFromTopUp > 0
                  ? t("captainBilling.tiles.fromTopUpOf", {
                    tokens: format(account.topUpBalanceTokens),
                    total: format(account.topUpBalanceTokens + spentFromTopUp),
                  })
                  : t("captainBilling.tiles.fromTopUp", { tokens: format(account.topUpBalanceTokens) })} />
              </p>
            ) : (
              <StatusLine tone={state === "low" ? "warning" : "error"}>{statusText}</StatusLine>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </Tile>
  );
}

type TokenFormat = (tokens: number) => string;

function LegendItem({ source, text }: { source: CaptainBillingHistorySource; text: (format: TokenFormat) => string }) {
  const formatTokens = useTokenFormat();
  const shortTokens = useShortTokenFormat();
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap" title={text(formatTokens)}>
      <BalanceSwatch source={source} />
      {text(shortTokens)}
    </span>
  );
}
